import { CopyEntity, ExistingMode } from '../types/copy-paste.types';
import { buildCopyPayload, countCopyResults } from '../utils/copy-paste.utils';
import { useCallback, useState } from 'react';

import APIService from '../../../../../services/api.service';
import { SubItem } from '../../../types/sub-item.types';
import { ToastService } from '../../../../../services/toast.service';
import { clearCopiedParts } from '../../../state/copy-paste.state';
import { useQueryClient } from 'react-query';
import { useTableDataState } from '../../../state/table-data.state';

interface UsePasteSubmitParams {
   copiedParts: SubItem[];
   verifiedPartIds: string[];
   verifiedItemNos: string[];
   selectedCopyEntities: CopyEntity[];
   existingMode: ExistingMode;
   createMissing: boolean;
   onClose: () => void;
}

export const usePasteSubmit = ({
   copiedParts,
   verifiedPartIds,
   verifiedItemNos,
   selectedCopyEntities,
   existingMode,
   createMissing,
   onClose,
}: UsePasteSubmitParams) => {
   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const submitCopyPaste = useCallback(async () => {
      if (verifiedPartIds.length === 0 || verifiedItemNos.length === 0) {
         ToastService.warning('Please select at least one part and one target item');
         return;
      }
      if (selectedCopyEntities.length === 0) {
         ToastService.warning('Please select at least one entity to copy');
         return;
      }
      if (existingMode === 'skip' && !createMissing) {
         ToastService.warning('Nothing to paste — skipping existing and not creating missing.');
         return;
      }

      setIsSubmitting(true);

      const payload = buildCopyPayload({
         copiedParts,
         verifiedPartIds,
         verifiedItemNos,
         selectedCopyEntities,
         existingMode,
         createMissing,
      });

      try {
         const res = await APIService.copyParts(payload);
         const { copiedCount, pastedCount } = countCopyResults(res);

         if (copiedCount + pastedCount === 0) {
            ToastService.info('No parts were copied');
         } else {
            ToastService.success(`${copiedCount} part(s) copied into ${pastedCount} item(s)`);
         }

         // Do not await refetch; close on paste success.
         void queryClient.invalidateQueries(['data', query]);
         clearCopiedParts();
         onClose();
      } catch (e) {
         console.error('Copy parts failed:', e);
         ToastService.error('Something went wrong. Could not copy parts');
      } finally {
         setIsSubmitting(false);
      }
   }, [
      copiedParts,
      verifiedPartIds,
      verifiedItemNos,
      selectedCopyEntities,
      existingMode,
      createMissing,
      onClose,
      queryClient,
      query,
   ]);

   return { isSubmitting, submitCopyPaste };
};
